import { Command, Option } from 'commander'
import { createClient } from '../../../client.js'
import { handleError } from '../../../lib/errors.js'
import {
  isQuiet,
  printQuiet,
  printJson,
  printSuccess,
  printKeyValue,
} from '../../../output.js'
import { pollUntilDone } from '../../../lib/polling.js'

// Keep in sync with REGION_LABELS from shared/constants.ts
const REGION_LABELS: Record<string, string> = {
  'us-east-1': `US East (N. Virginia)`,
  'eu-west-1': `EU West (Ireland)`,
  'ca-west-1': `CA West (Canada)`,
}

export const servicesCreatePostgresCommand = new Command(`postgres`)
  .usage(`--environment <id> --database-url <url> --region <region> [options]`)
  .description(`Connect a Postgres database for sync`)
  .requiredOption(`--environment <id>`, `Environment ID`)
  .requiredOption(`--database-url <url>`, `Postgres connection string`)
  .addOption(
    new Option(`--region <region>`, `Deployment region`)
      .choices(Object.keys(REGION_LABELS))
      .makeOptionMandatory()
  )
  .option(`--name <name>`, `Service name`)
  .option(`--pooled-database-url <url>`, `Pooled Postgres connection string`)
  .option(
    `--db-pool-size <size>`,
    `Database connection pool size (1-20)`,
    parseInt
  )
  .option(`--manual-table-publishing`, `Opt out of automatic table publishing`)
  .option(
    `--replication-idle-timeout <seconds>`,
    `Idle replication connection timeout in seconds (0 to disable; recommended for Neon)`,
    parseInt
  )
  .option(`--wait`, `Wait for service to become active before returning`)
  .option(
    `--wait-timeout <seconds>`,
    `Timeout in seconds when using --wait`,
    `300`
  )
  .addHelpText(
    `after`,
    `\nRegions:\n` +
      Object.entries(REGION_LABELS)
        .map(([k, v]) => `  ${k}  ${v}`)
        .join(`\n`)
  )
  .action(async (opts) => {
    const json =
      servicesCreatePostgresCommand.parent?.parent?.parent?.opts().json ?? false
    try {
      const client = createClient()
      const options: Record<string, unknown> = {}
      if (opts.pooledDatabaseUrl)
        options.pooledDatabaseUrl = opts.pooledDatabaseUrl
      if (opts.dbPoolSize) options.dbPoolSize = opts.dbPoolSize
      if (opts.manualTablePublishing) options.manualTablePublishing = true
      if (opts.replicationIdleTimeout !== undefined)
        options.replicationIdleTimeout = opts.replicationIdleTimeout

      const result = await client.services.postgres.create({
        environmentId: opts.environment,
        databaseUrl: opts.databaseUrl,
        region: opts.region,
        ...(opts.name ? { serviceName: opts.name } : {}),
        ...(Object.keys(options).length > 0 ? { options } : {}),
      })

      const serviceId = result.id
      let finalStatus = result.status

      if (opts.wait && finalStatus !== `active` && finalStatus !== `error`) {
        if (!isQuiet() && !json) {
          process.stderr.write(`Waiting for service to become active...\n`)
        }

        const polled = await pollUntilDone({
          fn: async () => {
            const service = await client.services.postgres.get({ serviceId })
            const status = service.status
            if (status === `active` || status === `error`) {
              return { done: true, value: status }
            }
            return { done: false, value: status ?? `pending` }
          },
          timeoutSeconds: isNaN(parseInt(opts.waitTimeout, 10))
            ? 300
            : parseInt(opts.waitTimeout, 10),
          onPoll: (elapsed) => {
            if (!isQuiet() && !json) {
              process.stderr.write(
                `  Waiting for service (${elapsed}s elapsed)...\n`
              )
            }
          },
        })

        finalStatus = polled
      }

      if (finalStatus === `error`) {
        if (json) {
          printJson({ ...result, status: finalStatus })
        } else if (!isQuiet()) {
          process.stderr.write(`Service entered error state.\n`)
          printKeyValue([
            [`ID:`, serviceId],
            [`Status:`, `error`],
          ])
        }
        process.exit(1)
      }

      if (isQuiet()) {
        printQuiet(serviceId)
        return
      }

      if (json) {
        printJson(opts.wait ? { ...result, status: finalStatus } : result)
      } else {
        printSuccess(`Service created successfully.\n`)
        printKeyValue([
          [`ID:`, serviceId],
          [`Status:`, finalStatus],
          [`Secret:`, result.sourceSecret],
        ])
      }
    } catch (error) {
      await handleError(error, json)
    }
  })
